/**
 * Social Media — the founder's ever-growing archive of copy-paste ready
 * social assets for @iSpeedToClose.
 *
 * Layout: tabbed sections. Every text block has a one-click copy button.
 * When Tracy asks for new social copy in chat, the E1 agent appends it
 * to the relevant tab here so nothing ever gets lost in scroll.
 *
 * Sections:
 *   1. Reply Templates — paste-ready replies for engaging with top RE voices
 *   2. Pinned Tweet Bank — pinned-post drafts (rotate as milestones hit)
 *   3. Post Bank — regular posts, organized by angle (value / BTS / launch / hot take)
 *   4. Profile Copy — bio variants, taglines, cover image notes
 *   5. Follow Strategy — the 4-circle plan + curated handles to follow
 */
import { useMemo, useState } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Copy, CheckCircle2, MessageCircle, Pin, Sparkles, User, Users,
  ExternalLink, Info,
} from "lucide-react";

/* ─────────────────────────── copy helper ─────────────────────────── */
function CopyBlock({ text, testid, muted = false, footnote }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <div className="space-y-1">
      <div
        className={
          "relative rounded-lg border border-[var(--line)] p-4 pr-14 text-[13px] leading-6 whitespace-pre-wrap " +
          (muted ? "bg-slate-50 text-[var(--ink-2)] italic" : "bg-slate-50")
        }
        data-testid={testid}
      >
        {text}
        <button
          type="button"
          onClick={copy}
          className="absolute top-3 right-3 p-2 rounded-md hover:bg-white border border-[var(--line)] transition"
          data-testid={`${testid}-copy`}
          aria-label="Copy to clipboard"
        >
          {copied
            ? <CheckCircle2 size={16} className="text-emerald-500" />
            : <Copy size={16} className="text-[var(--ink-2)]" />}
        </button>
      </div>
      {footnote && (
        <div className="text-[11px] text-[var(--ink-3)] italic pl-1">{footnote}</div>
      )}
    </div>
  );
}

function CharCount({ text }) {
  return (
    <span className="text-[10px] font-mono text-[var(--ink-3)] uppercase tracking-widest">
      {text.length} chars
    </span>
  );
}

/* ─────────────────────────── section wrapper ─────────────────────────── */
function Section({ icon: Icon, title, subtitle, badge, children, testid }) {
  return (
    <Card data-testid={testid} className="border-[var(--line)]">
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-base font-semibold">
              <Icon size={18} className="text-[var(--primary)]" />
              {title}
            </CardTitle>
            {subtitle && (
              <p className="text-xs text-[var(--ink-2)] mt-1">{subtitle}</p>
            )}
          </div>
          {badge && (
            <Badge className="bg-amber-100 text-amber-900 border border-amber-200 shrink-0">
              {badge}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">{children}</CardContent>
    </Card>
  );
}

/* ─────────────────────────── DATA ─────────────────────────── */

const REPLY_TEMPLATES = [
  {
    id: "pace-morby",
    handle: "@pacemorby",
    context: "When Pace posts a creative-finance breakdown or a wholesale case study.",
    template:
`This is the part every YouTube course leaves out — the actual sequence of who signs what, when. Saving this thread. Pace's playbook is one of the reasons we built @iSpeedToClose to hold every doc inside one CRM.`,
    tone: "Warm + specific + light plug",
  },
  {
    id: "jamil-damji",
    handle: "@jamildamji",
    context: "When Jamil posts about dispositions, buyer lists, or triple-net wholesaling.",
    template:
`Dispositions is where most new wholesalers actually lose the deal — not acquisitions. Your point about vetted buyer lists is why we built the Cash Buyer Black Book into @iSpeedToClose. Free playbook for anyone who wants the vetting rubric: ispeedtoclose.com/blackbook`,
    tone: "Add value + soft link",
  },
  {
    id: "cody-sperber",
    handle: "@codysperber",
    context: "When Cody drops market data or a mindset thread.",
    template:
`This maps exactly to what we're seeing in the @iSpeedToClose data — assignment fees are compressing but volume is up in the same markets. The operators who scale are the ones who systematize the boring parts (skip trace, dialer, docs) so they can focus on the seller conversation.`,
    tone: "Data-forward, peer-to-peer",
  },
  {
    id: "grant-cardone",
    handle: "@GrantCardone",
    context: "When Grant posts about scale or the '10X' framing.",
    template:
`10X is the mindset. But most wholesalers can't 10X because they're still running deals in spreadsheets and screenshots. The unlock isn't more effort — it's an operating system. Built @iSpeedToClose exactly for this bottleneck.`,
    tone: "Reframe + specific bottleneck",
  },
  {
    id: "biggerpockets",
    handle: "@BiggerPockets",
    context: "When BP posts wholesaling / RE investing content.",
    template:
`For the wholesalers here — we built a free 12-book library covering everything BP touches on but from an operator's angle (wholesaling 101, 25 seller objections, TCPA survival, cash buyer vetting). Zero email gate: ispeedtoclose.com/library`,
    tone: "Community-value pitch",
  },
  {
    id: "generic-newbie",
    handle: "Any new wholesaler asking a beginner question",
    context: "When you find a low-follower wholesaler asking for scripts, comps advice, or DNC help.",
    template:
`I've been where you are. We put a free 40-page cold call script book and a 25-objection playbook up at ispeedtoclose.com/library — no email required. Whatever your first deal ends up looking like, those two docs will save you 3 months.`,
    tone: "Mentor voice, high generosity, no signup ask",
  },
];

const PINNED_TWEET_VARIANTS = [
  {
    id: "founder-story",
    label: "Variant 1 — Founder story",
    tone: "Warm, disarming, personal",
    text:
`I built iSpeedToClose because I got tired of losing wholesale deals in screenshots and notebooks.

AI voice agent. Dialer. CRM. Buyer black book. 12 free playbooks. Every tool a wholesaler needs — one login.

14-day free trial, no card. → ispeedtoclose.com`,
    media: "Attach the X header image or a pipeline kanban screenshot.",
    why: "Humanizes @iSpeedToClose. Best if you reply from the account in your own voice.",
  },
  {
    id: "product-demo",
    label: "Variant 2 — Product demo (video-forward)",
    tone: "Demo-driven, highest CTR",
    text:
`This is what happens when a seller calls at 11pm and you're asleep.

Apex 2.0 answers. Qualifies. Books the callback. Books the property visit. All while you sleep.

Watch a real 45-second call → [drop demo video URL when recorded]

Try free → ispeedtoclose.com`,
    media: "45-sec Apex demo video (blocks on Tracy recording it).",
    why: "Video pins get 3–5× the impressions of text-only. Apex is your single strongest differentiator.",
  },
  {
    id: "free-library",
    label: "Variant 3 — Free library flex ⭐ recommended for now",
    tone: "Top-of-funnel volume maximizer",
    text:
`12 free playbooks. Zero email required. No upsell.

Wholesaling 101. Cold call scripts. 25 seller objections. Cash buyer black book. TCPA survival. After the first check. And 6 more.

Read the whole library → ispeedtoclose.com/library`,
    media: "2×3 grid of the 12 ebook covers (ask E1 to generate).",
    why: "Nobody else in the wholesaling SaaS space gives away this much content. It's your unfair advantage.",
  },
  {
    id: "anti-guru",
    label: "Variant 4 — Anti-guru positioning",
    tone: "Sharpest / riskiest",
    text:
`Wholesaling doesn't need another $5,000 coaching program.

It needs an operating system: AI voice agent, dialer, CRM, buyer list, 12 free playbooks. Built by an operator, not a coach.

Prove me wrong for 14 days, free → ispeedtoclose.com`,
    media: "Screenshot of app dashboard (real numbers, not mock).",
    why: "Takes a stance. Alienates gurus (fine, they're not your buyer). Highest engagement per impression but lower total volume.",
  },
  {
    id: "thread-hook",
    label: "Variant 5 — Thread hook (7 tweets)",
    tone: "Long-term compounding asset",
    text:
`I quit my W-2 wholesaling houses. Then I built the software I wish existed.

Here's what's inside iSpeedToClose — the operating system for wholesalers who close, not just study:

🧵`,
    media: "Single header image on tweet 1; product screenshots on 2/3/4.",
    why: "Threads currently get algorithmic priority on X. One tweet becomes 7 impressions per visitor. Best long-term ROI.",
  },
];

const THREAD_CONTINUATIONS = [
  {
    id: "thread-1",
    label: "Thread — tweet 2 of 7",
    text:
`2/ Apex 2.0 answers your inbound seller calls, 24/7.

Qualifies them. Books the callback on your calendar. Books the property visit.

The dialer never sleeps. You do.`,
  },
  {
    id: "thread-2",
    label: "Thread — tweet 3 of 7",
    text:
`3/ Full pipeline kanban.

Every deal. Every stage. Every touchpoint.

Zero screenshots, zero lost leads, zero "I'll follow up tomorrow" (you won't).`,
  },
  {
    id: "thread-3",
    label: "Thread — tweet 4 of 7",
    text:
`4/ 12 free playbooks. No email required.

Wholesaling 101. Cold call scripts. 25 seller objections. Cash buyer black book. TCPA survival. And 7 more.

ispeedtoclose.com/library`,
  },
  {
    id: "thread-4",
    label: "Thread — tweet 5 of 7",
    text:
`5/ Buyer Black Book.

Vet, rank, and retain 30–50 verified cash buyers per market.

Because deals close on the disposition side, not the acquisition side.`,
  },
  {
    id: "thread-5",
    label: "Thread — tweet 6 of 7",
    text:
`6/ Twilio-native dialer + SMS + voice.

TCPA compliance baked in. DNC scrub on every send. Consent tracked on every lead.

Stop worrying about lawsuits. Start dialing.`,
  },
  {
    id: "thread-6",
    label: "Thread — tweet 7 of 7 (the CTA)",
    text:
`7/ 14 days free. No card.

If you close a deal in your trial, you keep the software free for a month.

That's how confident I am → ispeedtoclose.com`,
  },
];

const DISPO_PROBLEM_THREAD = [
  {
    id: "dispo-1",
    label: "Dispo Problem — tweet 1 (HOOK, pinnable)",
    text:
`Unpopular take:

90% of wholesaling content is about acquisitions.
90% of wholesalers fail at dispositions.

Nobody's building tools for the actual bottleneck.

Here's why dispo is broken — and what I'm doing about it 🧵`,
  },
  {
    id: "dispo-2",
    label: "Dispo Problem — tweet 2",
    text:
`2/ Every guru sells you the acquisitions dream:
 · Cold call 100 sellers
 · Get 3 appointments
 · Sign 1 contract

They stop the story right there.

Because the next part — actually SELLING that contract — is where most new wholesalers quietly quit.`,
  },
  {
    id: "dispo-3",
    label: "Dispo Problem — tweet 3",
    text:
`3/ The typical dispo workflow in 2026:

 · Google Drive link with 12 phone-camera photos
 · WhatsApp blast to 87 "cash buyers" (60% aren't real)
 · Offer negotiations happening in 14 different text threads
 · "Best and final by Friday" (nobody replies)

It's medieval.`,
  },
  {
    id: "dispo-4",
    label: "Dispo Problem — tweet 4",
    text:
`4/ Meanwhile the top 5% of wholesalers have:

 · Private Deal Rooms per property
 · Vetted buyer lists that self-signup with preferences
 · Match-based auto-alerts (60 sec from listing to buyer SMS)
 · In-app offer submission with ranked leaderboard
 · Auto-counter engines

The gap isn't skill. It's software.`,
  },
  {
    id: "dispo-5",
    label: "Dispo Problem — tweet 5",
    text:
`5/ So I built it.

Dispo Studio — the 3-feature bundle that closes the dispo gap:

 · Private Deal Room per property
 · Buyer match notifications
 · In-app offer auction leaderboard

Launching inside every iSpeedToClose plan. No upsell, no add-on fee. Bundled.`,
  },
  {
    id: "dispo-6",
    label: "Dispo Problem — tweet 6",
    text:
`6/ Basic ($97/mo): 5 Deal Rooms + public buyer signup page. Manual work, but you'll look pro.

Pro ($197/mo): Unlimited Deal Rooms + match notifications + offer auction + auto-counter engine + 1099 export.

Same price you're already paying. Now with the dispo side handled.`,
  },
  {
    id: "dispo-7",
    label: "Dispo Problem — tweet 7 (CTA)",
    text:
`7/ First 50 waitlisters get Dispo Studio Pro grandfathered at their current plan price — forever.

Even when we raise plans, waitlist members keep their price.

Waitlist here → ispeedtoclose.com/dispo-studio

The acquisitions dream is only half the wholesaling story.`,
  },
];

const DISPO_LAUNCH_POSTS = [
  {
    id: "dispo-instagram-carousel",
    label: "Instagram — carousel caption (pairs with 10-slide deck)",
    text:
`90% of wholesaling content teaches you how to FIND deals.

Nobody teaches you how to SELL them.

That's why 60% of new wholesalers quit after deal #1. Not because they can't get contracts. Because they can't move them.

Dispo Studio is the fix. Deal Rooms · Match Notifications · Offer Auction.

Bundled into iSpeedToClose. No upsell. First 50 waitlisters get grandfathered pricing forever.

Link in bio 🔗

.
.
.
#realestate #wholesaling #realestateinvesting #dispositions #wholesalerealestate #realestateeducation #realestatetips #propertyinvestor #realestatelife #realestatetech`,
  },
  {
    id: "dispo-facebook-longform",
    label: "Facebook — long-form launch announcement",
    text:
`Big update from iSpeedToClose HQ 📣

For 6 months, every feature I've shipped has been about ONE side of wholesaling: acquisitions. Apex 2.0 answers your seller calls. AI Lead Scoring ranks your inbound leads. The Cash Buyer Black Book tracks who to sell to.

But here's what nobody talks about:

The moment you sign that seller contract... the actual hard part begins. You have 30-45 days to find an end buyer, negotiate the assignment, collect EMD, and get to close. This is where 60% of new wholesalers quit.

Nobody's building software for it.

Until now.

Introducing Dispo Studio — 3 features that finish the wholesaling workflow:

1. Private Deal Room — one branded URL per deal. Photos, comps, ARV, repair est, video walkthrough, "Make Offer" button.

2. Match Notifications — buyers self-signup on your public page with their zips + price + property type. Every new Deal Room auto-alerts matching buyers by SMS in 60 seconds.

3. Offer Auction Leaderboard — buyers offer in-app. You see a ranked leaderboard. Optional anonymized top-3 creates auction pressure.

Bundled into your existing iSpeedToClose plan. No extra charge. Basic ($97/mo) gets 5 Deal Rooms + signup page. Pro ($197/mo) gets unlimited Deal Rooms + full automation.

Waitlist is open. First 50 get grandfathered pricing forever, even when we raise plans.

Comment DISPO below and I'll DM you the waitlist link 🔗`,
  },
  {
    id: "dispo-linkedin-launch",
    label: "LinkedIn — launch post (skipping for now, but here's the copy)",
    text:
`A confession about the real estate wholesaling industry: we've built an entire education economy around finding deals — and left the ACTUAL bottleneck completely underserved.

Every wholesaling course, coach, and mastermind focuses on acquisitions:
· Cold calling scripts
· Direct mail templates
· Motivated seller psychology
· Contract-to-close training

And yet 60% of new wholesalers quit AFTER they sign their first contract. Because the dispo workflow in 2026 is still:
· Google Drive links
· WhatsApp blasts to unverified "cash buyers"
· Offers negotiated in 14 different text threads

I've been building iSpeedToClose for wholesalers for 6 months. Today, we're announcing Dispo Studio — a 3-feature bundle that closes the gap:

→ Private Deal Room per property (public shareable URL with photos, comps, ARV, video, "Make Offer" button)

→ Match Notifications (buyers self-signup with preferences; new deals auto-alert matching buyers in 60 seconds)

→ Offer Auction Leaderboard (ranked offers, optional auction dynamic, auto-counter engine)

Bundled into every iSpeedToClose plan starting next month. No add-on fees. Same price you're paying now.

If you're a wholesaler tired of losing deals to weak dispo — waitlist is open (first 50 get grandfathered pricing forever). Link in the first comment.

#RealEstate #Wholesaling #PropTech #SaaS`,
  },
  {
    id: "dispo-x-single",
    label: "X — single tweet variant (short + punchy)",
    text:
`Every guru: "Here's how to get your first wholesale deal."

Me: "Here's how to actually sell it in 4 hours instead of 4 weeks."

Dispo Studio launches soon. Waitlist → ispeedtoclose.com/dispo-studio`,
  },
];

const BIO_VARIANTS = [
  {
    id: "bio-a",
    label: "Variant A — Punchy operator (Hormozi/Bloom energy)",
    text: "The operating system real estate wholesalers actually run. AI voice agent, dialer, CRM, buyer black book. 14-day free trial — no card.",
    note: "Anchors on the product. Best if the profile feels like a company account.",
  },
  {
    id: "bio-b",
    label: "Variant B — Founder voice",
    text: "Founder @tperrin. Building the tools I wish I had closing my first assignment. AI voice agent, dialer, CRM for wholesalers. 14-day free trial.",
    note: "Humanizes the account. Best for personal-brand replies.",
  },
  {
    id: "bio-c",
    label: "Variant C — Lead-magnet loop ⭐ currently live",
    text: "12 free playbooks for real estate wholesalers, then the tools that run the deals. AI dialer, CRM, buyer black book. Deal #1 to deal #100, one login.",
    note: "Plugs your biggest content moat straight into the bio. Highest top-of-funnel volume.",
  },
  {
    id: "bio-d",
    label: "Variant D — Anti-guru",
    text: "For the wholesaler doing it without a guru. AI voice agent, dialer, CRM, 12 free playbooks. Built by an operator, not a coach. Try free.",
    note: "Sharpest positioning. Trades broad appeal for a strong hook.",
  },
  {
    id: "bio-e",
    label: "Variant E — Numbers/proof-forward",
    text: "12 free playbooks · AI voice agent · dialer · CRM · buyer black book · marketplace. Every tool a wholesaler needs, one login. 14-day free trial.",
    note: "Reads like a Product Hunt tagline. Good if the header image is minimal.",
  },
];

const FOLLOW_STRATEGY = {
  circles: [
    {
      title: "🎯 Circle 1 — Active wholesalers who post",
      why: "These are your customers. Follow → engage → they notice @iSpeedToClose.",
      handles: [
        { h: "@pacemorby", note: "Creative finance / wholesaling. Massive following, replies to newcomers." },
        { h: "@jamildamji", note: "AstroFlipping founder. Wholesaling-first content." },
        { h: "@codysperber", note: "Clever Investor. Veteran wholesaler." },
        { h: "@wholesalinghouses", note: "Pure wholesaling content." },
      ],
    },
    {
      title: "🎯 Circle 2 — Real estate investors (buyer-side)",
      why: "Not wholesalers, but buyers of your wholesalers' deals — potential Marketplace users.",
      handles: [
        { h: "@BiggerPockets", note: "Category-defining account." },
        { h: "@GrantCardone", note: "Big RE investing following." },
        { h: "@codie_sanchez", note: "Small business + RE investing overlap." },
        { h: "@thejlcollins", note: "Long-form RE investing philosophy." },
      ],
    },
    {
      title: "🎯 Circle 3 — PropTech + SaaS founders",
      why: "Peer network. Will amplify you + you'll learn their playbooks.",
      handles: [
        { h: "@marc_louvion", note: "Marc Lou — solo SaaS founder, generous, huge audience." },
        { h: "@levelsio", note: "Pieter Levels — indie-hacker legend." },
        { h: "@dvassallo", note: "SaaS founder who writes about pricing/positioning." },
        { h: "@shl", note: "Sahil Bloom — writes about business + RE exposure." },
      ],
    },
    {
      title: "🎯 Circle 4 — Journalists + industry voices",
      why: "For future press. Follow a small handful, engage occasionally.",
      handles: [
        { h: "@therealdeal", note: "Real estate news." },
        { h: "Local RE journalists in your top wholesaling markets", note: "Search their name + your target metro." },
      ],
    },
  ],
  rules: [
    "Follow ~50 people this week. Curated > big.",
    "Reply to their posts with genuine value — never links to yours in the first month.",
    "Never DM cold pitches in your first 30 days. Kills goodwill fast.",
    "Turn on notifications for 5–10 accounts in Circle 1. Reply first when they post → their audience sees you.",
    "Every 2 weeks, prune. Unfollow anyone silent >60 days. Silent follows waste algorithmic signal.",
  ],
};

const PROFILE_ASSETS = [
  { label: "iSpeedToClose Badge (1254×1254) — canonical logo", url: "/brand/badge.png" },
  { label: "X Avatar (400×400)", url: "/brand/x-avatar.png" },
  { label: "X Header (1500×500)", url: "/brand/x-header.png" },
  { label: "Facebook Profile Picture (400×400)", url: "/brand/fb-avatar.png" },
  { label: "Facebook Cover Photo (851×315)", url: "/brand/fb-cover.png" },
  { label: "Instagram Library Grid (1080×1350)", url: "/brand/ig-library-grid.png" },
  { label: "YouTube Channel Banner (2560×1440)", url: "/brand/yt-banner.png" },
  { label: "Wordmark (color, on white)", url: "/brand/wordmark-color.png" },
  { label: "Wordmark (white, for dark backgrounds)", url: "/brand/wordmark-white.png" },
  { label: "Standalone Mark (color)", url: "/brand/mark.png" },
];

const LIVE_PROFILES = [
  { platform: "X (Twitter)", handle: "@iSpeedToClose", url: "https://x.com/iSpeedToClose", status: "live" },
  { platform: "Facebook", handle: "iSpeedToClose", url: "https://facebook.com/iSpeedToClose", status: "live" },
  { platform: "Instagram", handle: "iSpeedToClose", url: "https://instagram.com/iSpeedToClose", status: "live" },
  { platform: "YouTube", handle: "@iSpeedToClose", url: "https://youtube.com/@iSpeedToClose", status: "live" },
];

/* ─────────────────────────── Tabs ─────────────────────────── */
const TABS = [
  { id: "replies", label: "Reply Templates", icon: MessageCircle },
  { id: "pinned", label: "Pinned Tweet Bank", icon: Pin },
  { id: "posts", label: "Post Bank", icon: Sparkles },
  { id: "profile", label: "Profile Copy", icon: User },
  { id: "follow", label: "Follow Strategy", icon: Users },
];

/* ─────────────────────────── Main page ─────────────────────────── */
export default function AdminSocialMedia() {
  const [tab, setTab] = useState("replies");
  const current = useMemo(() => TABS.find((t) => t.id === tab), [tab]);

  return (
    <div className="space-y-6" data-testid="admin-social-media">
      {/* Header */}
      <div className="rounded-2xl border border-[var(--line)] bg-gradient-to-br from-[#0B1220] via-[#152346] to-[#0B1220] text-white p-6 md:p-8 relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-[#F59E0B]/15 blur-3xl pointer-events-none" />
        <div className="relative">
          <div className="flex items-center gap-2 mb-2 text-[#F59E0B] text-[10px] font-black tracking-[0.18em] uppercase">
            <Sparkles size={12} /> Social Media Command Center
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold leading-tight">
            Every post, every reply, <span className="text-[#F59E0B]">one archive.</span>
          </h1>
          <p className="text-slate-300 text-sm md:text-base leading-relaxed mt-3 max-w-2xl">
            The ever-growing library of copy-paste-ready social assets for
            <span className="text-white font-semibold"> @iSpeedToClose</span>.
            When you ask E1 for new social copy in chat, it lands here so it never gets lost in scroll.
          </p>
          <div className="text-slate-400 text-xs mt-4 flex items-center gap-1.5">
            <Info size={12} /> Every text block has a one-click copy button. Just paste into X, LinkedIn, or wherever.
          </div>
        </div>
      </div>

      {/* Tab strip */}
      <div className="flex flex-wrap gap-2" data-testid="social-tab-strip">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            data-testid={`social-tab-${t.id}`}
            className={
              "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide transition " +
              (tab === t.id
                ? "bg-[var(--ink)] text-white"
                : "bg-stone-100 text-stone-600 hover:bg-stone-200")
            }
          >
            <t.icon size={13} />
            {t.label}
          </button>
        ))}
      </div>

      {/* Panels */}
      {tab === "replies" && (
        <Section
          icon={MessageCircle}
          title="Reply Templates"
          subtitle="Paste-ready replies for engaging with top real-estate voices. Never stare at the reply box again."
          badge={`${REPLY_TEMPLATES.length} templates`}
          testid="social-panel-replies"
        >
          {REPLY_TEMPLATES.map((r) => (
            <div key={r.id} className="space-y-2 pb-4 border-b border-[var(--line)] last:border-0 last:pb-0">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div>
                  <div className="font-bold text-sm">{r.handle}</div>
                  <div className="text-[11px] text-[var(--ink-3)] italic">{r.context}</div>
                </div>
                <Badge className="bg-slate-100 text-slate-700 border border-slate-200 text-[10px] font-bold uppercase tracking-widest">
                  {r.tone}
                </Badge>
              </div>
              <CopyBlock text={r.template} testid={`reply-${r.id}`} />
            </div>
          ))}
        </Section>
      )}

      {tab === "pinned" && (
        <>
          <Section
            icon={Pin}
            title="Pinned Tweet Variants"
            subtitle="5 angles for your pinned post. Rotate as milestones hit (demo video → variant 2, first 1K followers → variant 4)."
            badge={`${PINNED_TWEET_VARIANTS.length} variants`}
            testid="social-panel-pinned"
          >
            {PINNED_TWEET_VARIANTS.map((p) => (
              <div key={p.id} className="space-y-2 pb-4 border-b border-[var(--line)] last:border-0 last:pb-0">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div>
                    <div className="font-bold text-sm">{p.label}</div>
                    <div className="text-[11px] text-[var(--ink-3)] italic">{p.tone}</div>
                  </div>
                  <CharCount text={p.text} />
                </div>
                <CopyBlock text={p.text} testid={`pinned-${p.id}`} footnote={`Attach: ${p.media}`} />
                <div className="text-[11px] text-[var(--ink-2)] leading-relaxed pl-1"><b>Why it works:</b> {p.why}</div>
              </div>
            ))}
          </Section>

          <Section
            icon={Sparkles}
            title="Thread continuations (for Variant 5)"
            subtitle="If you pin the thread hook, these are the 6 follow-up tweets in order."
            badge={`${THREAD_CONTINUATIONS.length} tweets`}
            testid="social-panel-thread"
          >
            {THREAD_CONTINUATIONS.map((t) => (
              <div key={t.id} className="space-y-2 pb-4 border-b border-[var(--line)] last:border-0 last:pb-0">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-bold text-sm">{t.label}</div>
                  <CharCount text={t.text} />
                </div>
                <CopyBlock text={t.text} testid={`thread-${t.id}`} />
              </div>
            ))}
          </Section>
        </>
      )}

      {tab === "posts" && (
        <>
          <Section
            icon={Sparkles}
            title="🎯 Dispo Studio Launch Kit"
            subtitle="Coordinated launch copy across every channel. Use in order: X thread → Instagram carousel → Facebook long-form → single X follow-up."
            badge={`${DISPO_LAUNCH_POSTS.length} posts · launch-ready`}
            testid="social-panel-dispo-launch"
          >
            {DISPO_LAUNCH_POSTS.map((p) => (
              <div key={p.id} className="space-y-2 pb-4 border-b border-[var(--line)] last:border-0 last:pb-0">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="font-bold text-sm">{p.label}</div>
                  <CharCount text={p.text} />
                </div>
                <CopyBlock text={p.text} testid={`dispo-post-${p.id}`} />
              </div>
            ))}
          </Section>

          <Section
            icon={Pin}
            title="🔥 The Dispo Problem — 7-tweet thread"
            subtitle="The launch thesis. Publish this on X first — it seeds the story arc for every other post. Pinnable."
            badge={`${DISPO_PROBLEM_THREAD.length} tweets`}
            testid="social-panel-dispo-thread"
          >
            {DISPO_PROBLEM_THREAD.map((t) => (
              <div key={t.id} className="space-y-2 pb-4 border-b border-[var(--line)] last:border-0 last:pb-0">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="font-bold text-sm">{t.label}</div>
                  <CharCount text={t.text} />
                </div>
                <CopyBlock text={t.text} testid={`dispo-thread-${t.id}`} />
              </div>
            ))}
          </Section>

          <div className="mt-4 rounded-lg bg-amber-50 border border-amber-200 p-4">
            <div className="font-bold text-sm text-amber-900 mb-1">📌 Backlog reminder</div>
            <ul className="list-disc pl-5 space-y-1 text-[13px] text-amber-900 leading-relaxed">
              <li><b>Revisit FB cover v2</b> — current cover is temporary. Next version should be wholesaler-focused (not &quot;investors&quot;), mobile-safe, single-hook.</li>
              <li><b>Record 45-sec Apex 2.0 demo video</b> — unlocks pinned tweet Variant 2 (video-forward).</li>
              <li><b>Record 8-min Dispo Studio walkthrough</b> — launch-week YouTube hero video.</li>
              <li><b>Ebook #13: The Dispo Playbook</b> — 30-page guide launches WITH Dispo Studio (planned).</li>
              <li><b>Week 5 — Closing Team Module</b> — Title Companies + Transaction Coordinators (unified model, role-based). Also supports Attorneys / Inspectors / Appraisers / JV Partners as future extensions. Ships 2 weeks after Dispo Studio launch as v1.1 announcement.</li>
            </ul>
          </div>
        </>
      )}

      {tab === "profile" && (
        <>
          <Section
            icon={ExternalLink}
            title="Live Profiles"
            subtitle="Source of truth for every social handle. Ping E1 when a new one goes live."
            badge={`${LIVE_PROFILES.filter(p => p.status === 'live').length} live · ${LIVE_PROFILES.filter(p => p.status === 'pending').length} pending`}
            testid="social-panel-live-profiles"
          >
            <div className="grid sm:grid-cols-2 gap-3">
              {LIVE_PROFILES.map((p) => (
                <a
                  key={p.platform}
                  href={p.status === "live" ? p.url : undefined}
                  target={p.status === "live" ? "_blank" : undefined}
                  rel={p.status === "live" ? "noopener noreferrer" : undefined}
                  onClick={(e) => p.status !== "live" && e.preventDefault()}
                  className={
                    "flex items-center justify-between gap-2 p-3 rounded-lg border transition text-sm " +
                    (p.status === "live"
                      ? "border-[var(--line)] hover:border-[var(--primary)] hover:bg-[var(--primary)]/5"
                      : "border-dashed border-slate-300 opacity-70 cursor-not-allowed")
                  }
                  data-testid={`live-profile-${p.platform.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                >
                  <div className="min-w-0">
                    <div className="font-bold truncate">{p.platform}</div>
                    <div className="text-[11px] text-[var(--ink-3)] font-mono truncate">{p.handle}</div>
                  </div>
                  {p.status === "live" ? (
                    <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200 text-[10px] font-bold uppercase tracking-widest shrink-0">
                      Live
                    </Badge>
                  ) : (
                    <Badge className="bg-slate-100 text-slate-500 border border-slate-200 text-[10px] font-bold uppercase tracking-widest shrink-0">
                      Pending
                    </Badge>
                  )}
                </a>
              ))}
            </div>
          </Section>

          <Section
            icon={User}
            title="Bio Variants"
            subtitle="5 bio drafts under X's 160-char limit. Currently live: Variant C."
            badge={`${BIO_VARIANTS.length} bios`}
            testid="social-panel-bios"
          >
            {BIO_VARIANTS.map((b) => (
              <div key={b.id} className="space-y-2 pb-4 border-b border-[var(--line)] last:border-0 last:pb-0">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="font-bold text-sm">{b.label}</div>
                  <CharCount text={b.text} />
                </div>
                <CopyBlock text={b.text} testid={`bio-${b.id}`} />
                <div className="text-[11px] text-[var(--ink-2)] italic pl-1">{b.note}</div>
              </div>
            ))}
          </Section>

          <Section
            icon={User}
            title="Brand Assets"
            subtitle="Direct links to the correct image files (right-click → Save Image As)."
            badge={`${PROFILE_ASSETS.length} assets`}
            testid="social-panel-assets"
          >
            <div className="grid sm:grid-cols-2 gap-3">
              {PROFILE_ASSETS.map((a) => (
                <a
                  key={a.url}
                  href={a.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between gap-2 p-3 rounded-lg border border-[var(--line)] hover:border-[var(--primary)] hover:bg-[var(--primary)]/5 transition text-sm"
                  data-testid={`asset-${a.url.split("/").pop()}`}
                >
                  <span className="font-semibold">{a.label}</span>
                  <ExternalLink size={13} className="text-[var(--ink-3)]" />
                </a>
              ))}
            </div>
          </Section>
        </>
      )}

      {tab === "follow" && (
        <Section
          icon={Users}
          title="Follow Strategy"
          subtitle="Who to follow, in what order, and why. Not everyone — the ~50 that will actually move the needle."
          badge="4 circles"
          testid="social-panel-follow"
        >
          {FOLLOW_STRATEGY.circles.map((c) => (
            <div key={c.title} className="space-y-2 pb-4 border-b border-[var(--line)] last:border-0 last:pb-0">
              <div className="font-bold text-sm">{c.title}</div>
              <div className="text-[11px] text-[var(--ink-3)] italic">{c.why}</div>
              <ul className="mt-2 space-y-1.5">
                {c.handles.map((h) => (
                  <li key={h.h} className="flex flex-col sm:flex-row sm:items-baseline sm:gap-2 text-[13px]">
                    <span className="font-mono font-bold text-[var(--primary-dark)] shrink-0">{h.h}</span>
                    <span className="text-[var(--ink-2)]">{h.note}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          <div className="mt-2 rounded-lg bg-amber-50 border border-amber-200 p-4">
            <div className="font-bold text-sm text-amber-900 mb-2">Rules of engagement</div>
            <ol className="list-decimal pl-5 space-y-1 text-[13px] text-amber-900 leading-relaxed">
              {FOLLOW_STRATEGY.rules.map((r, i) => (<li key={i}>{r}</li>))}
            </ol>
          </div>
        </Section>
      )}
    </div>
  );
}
